import { useEffect, useCallback, useRef, useState } from 'react';

import { useRpc } from '@/lib/rpc';
import { MAX_PROCESSING_STALENESS_MS } from '@/lib/constants';
import type { Version } from '@/store/useVersionStore';

// Re-export Version type for consumers
export type { Version };

/**
 * Custom hook for managing version progress with simple polling
 *
 * This hook provides:
 * - Simple polling every 1 second when version processing is active (queued/pending/processing)
 * - Automatic start/stop of polling based on version status
 * - Proper cleanup of polling intervals
 * - Error handling and loading states
 * - LOCAL state to avoid conflicts between multiple consumers
 *
 * Note: This hook uses local state instead of a global store to prevent
 * race conditions when multiple components poll different versions simultaneously.
 */
export function useVersionProgress(versionId?: string) {
  const rpc = useRpc();

  // Use LOCAL state instead of global store to avoid conflicts
  // when multiple components use this hook with different version IDs
  const [currentVersion, setCurrentVersion] = useState<Version | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  // Track the versionId we're currently polling for to avoid stale updates
  const currentVersionIdRef = useRef<string | null>(null);
  // Keep fetchVersion stable across renders for use in polling interval
  const fetchVersionRef = useRef<(id: string) => Promise<Version | null>>(null!);

  /**
   * Fetch version data from API
   * Returns the version and whether it should continue polling
   */
  const fetchVersion = useCallback(async (id: string): Promise<Version | null> => {
    if (!id) return null;

    // Skip if this fetch is for a stale versionId
    if (currentVersionIdRef.current !== id) {
      return null;
    }

    setIsLoading(true);
    setError(null);

    try {
      // @ts-expect-error - Hono RPC type inference limitation for nested routes
      const res = await rpc.api.private.versions[':id'].$get({ param: { id } });

      if (!res.ok) {
        throw new Error('Failed to fetch version');
      }

      const data = await res.json();
      const version = data.version as Version;

      // Only update state if we're still polling for this version
      if (currentVersionIdRef.current === id) {
        setCurrentVersion(version);
      }
      return version;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch version';
      if (currentVersionIdRef.current === id) {
        setError(errorMessage);
      }
      return null;
    } finally {
      if (currentVersionIdRef.current === id) {
        setIsLoading(false);
      }
    }
  }, [rpc]);

  // Keep ref updated with latest fetchVersion for use in polling
  fetchVersionRef.current = fetchVersion;

  // Single effect to handle version ID changes and polling
  useEffect(() => {
    // Clear any existing polling
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }

    // Clear current version when versionId changes to prevent stale data flash
    setCurrentVersion(null);
    setError(null);
    currentVersionIdRef.current = versionId ?? null;

    if (!versionId) {
      return;
    }

    // Capture versionId for use in async callbacks to avoid stale closures
    const currentVersionId = versionId;

    // Initial fetch
    void fetchVersion(currentVersionId);

    // Start polling immediately - we poll until we know the version is complete/failed/stale/gone
    // Use ref to always call the latest fetchVersion (handles rpc changes)
    pollingIntervalRef.current = setInterval(async () => {
      const version = await fetchVersionRef.current?.(currentVersionId);

      // Stop polling if version was deleted (e.g. draft table cleaned up on failure)
      if (!version) {
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
        return;
      }

      const isTerminal = version.status === 'completed' || version.status === 'failed';
      // Stop polling for stale versions stuck in queued/processing for too long
      const isStale = !isTerminal && !!version.created_at &&
        (Date.now() - new Date(version.created_at).getTime()) > MAX_PROCESSING_STALENESS_MS;

      if (isTerminal || isStale) {
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
      }
    }, 1000);

    // Cleanup on unmount or versionId change
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      currentVersionIdRef.current = null;
    };
    // Note: We intentionally only depend on versionId to avoid restarting polling
    // when fetchVersion reference changes. The ref tracking in fetchVersion handles
    // stale version ID checks.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versionId]);

  const isPolling = pollingIntervalRef.current !== null;

  return {
    version: currentVersion,
    isLoading,
    error,
    isPolling,
    fetchVersion: () => versionId ? fetchVersion(versionId) : Promise.resolve(null),
  };
}
